import React, { useState } from "react"
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
} from "@material-ui/core"
import { CancelarPedido } from "../../../services/integracao"
import useAppStore from "../../../stores/AppStore"
import { listaCancelamento99food } from "../../RecebedorDePedidos"

type Motivo = { cod: string; description: string }

/** Dados mínimos do pedido (serve para o Relatório e o Histórico de Pedidos). */
export type PedidoCancelavel = { id: number; cliente?: string | null; plataforma?: string | null }

// Plataforma formatada ("99 Food", Relatório) ou crua ("didi_food", Histórico).
const ehPedido99Food = (plataforma?: string | null) => /99\s*food|didi/i.test(plataforma || "")

/**
 * Cancelamento de um pedido do Relatório de Pedidos: motivo (lista do estabelecimento)
 * + observação, via cancellationRequestedV2 — mesmo payload do Recebedor, mas sem mexer
 * nas listas do Recebedor (a tela recarrega a busca após cancelar).
 */
export function DialogCancelarPedido({
    pedido,
    onFechar,
    onCancelado,
    alertStart,
}: {
    pedido: PedidoCancelavel | null
    onFechar: () => void
    onCancelado: () => void
    alertStart: (msg: string, tipo: "success" | "error" | "info" | "warning") => void
}) {
    const parametros = useAppStore(s => s.configuracoes?.user?.parametros)
    // Pedidos 99Food usam os códigos de cancelamento da 99 (mesma regra do Recebedor).
    const motivos: Motivo[] =
        ehPedido99Food(pedido?.plataforma)
            ? listaCancelamento99food
            : parametros?.listaMotivoCancelamento || []

    const [indiceMotivo, setIndiceMotivo] = useState<string>("")
    const [observacao, setObservacao] = useState("")
    const [enviando, setEnviando] = useState(false)

    const fechar = () => {
        if (enviando) return
        setIndiceMotivo("")
        setObservacao("")
        onFechar()
    }

    const confirmar = async () => {
        if (!pedido) return
        if (motivos.length > 0 && indiceMotivo === "") {
            alertStart("Selecione o motivo do cancelamento.", "warning")
            return
        }
        if (observacao.trim().length <= 3) {
            alertStart("O motivo do cancelamento deve ter mais de 3 caracteres.", "warning")
            return
        }
        setEnviando(true)
        const motivo = indiceMotivo !== "" ? motivos[Number(indiceMotivo)] : undefined
        const resposta = await CancelarPedido(pedido.id, {
            observacao,
            motivoCancelamentoPedido: motivo ?? observacao,
        })
        setEnviando(false)
        if (!resposta || resposta.retornoErro || resposta.codErro > 0) {
            alertStart(resposta?.mensagem || "Não foi possível cancelar o pedido.", "error")
            return
        }
        alertStart(`Pedido #${pedido.id} cancelado.`, "success")
        setIndiceMotivo("")
        setObservacao("")
        onCancelado()
    }

    return (
        <Dialog open={!!pedido} onClose={fechar} maxWidth="xs" fullWidth>
            <DialogTitle>Cancelar pedido #{pedido?.id}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" style={{ color: "#7f8c8d", marginBottom: 12 }}>
                    {pedido?.cliente} — esta ação não pode ser desfeita.
                </Typography>
                {motivos.length > 0 && (
                    <TextField
                        select
                        label="Motivo"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={indiceMotivo}
                        onChange={e => setIndiceMotivo(e.target.value)}
                    >
                        {motivos.map((m, i) => (
                            <MenuItem key={m.cod ?? i} value={String(i)}>
                                {m.description}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
                <TextField
                    label="Observação"
                    multiline
                    fullWidth
                    minRows={3}
                    variant="outlined"
                    margin="normal"
                    value={observacao}
                    onChange={e => setObservacao(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={fechar} disabled={enviando}>
                    Voltar
                </Button>
                <Button
                    onClick={confirmar}
                    variant="contained"
                    disabled={enviando}
                    style={{ backgroundColor: "var(--cor-erro)", color: "white" }}
                    startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : undefined}
                >
                    Cancelar pedido
                </Button>
            </DialogActions>
        </Dialog>
    )
}
